import difference from 'lodash/difference'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import type moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import { RestDay, Shift, View } from 'modules/shift_management/lib/types'

const formatDate = (date: moment.Moment, view: View): string => {
  return dateToString(date, view.dateFormat)
}

const datesFromShift = (shift: Shift, view: View): string[] => {
  if (view.isDaily) {
    const hours = shift.hoursByReference(String(shift.id))
    return hours.map((h) => formatDate(h, view))
  }

  if (shift.isOvernightByCalendarDay) {
    return [formatDate(shift.currentStartDate, view), formatDate(shift.currentEndDate, view)]
  }

  return [formatDate(shift.currentStartDate, view)]
}

const getDaysFromEmployeeShifts = (shifts: Shift[] = [], view: View): string[] => {
  return uniq(shifts.flatMap((shift: Shift) => datesFromShift(shift, view)))
}

const getDaysFromEmployeeRestDays = (restDays: RestDay[] = [], view: View): string[] => {
  return uniq(restDays.flatMap((restDay: RestDay) => formatDate(restDay.currentDate, view)))
}

const selectedNightShiftsWithinRange = (shifts: Shift[], date: moment.Moment) => {
  return shifts.filter((shift: Shift) => shift.isNightShift && !shift.isBeginNightShift(date))
}

interface Params {
  view: View
  daysByRow: Record<string, string[]>
  shifts: Record<string, Shift>
  restDays: Record<string, RestDay>
}

const countCellsSelected = ({ view, daysByRow, shifts, restDays }: Params) => {
  const daysGrid = { ...daysByRow }
  const selectedShifts = Object.values(shifts)
  const selectedRestDays = Object.values(restDays)

  // If shift is a night shift and it is within the range, don't uniq it, each cell counts
  const nightShiftsWithinRange = selectedNightShiftsWithinRange(selectedShifts, view.lastDay)

  // If shift is regular day shift or a night shift that ends on the next period, uniq it
  const remainingShifts = difference(selectedShifts, nightShiftsWithinRange)

  let cellCount =
    nightShiftsWithinRange.length + uniq(remainingShifts).length + selectedRestDays.length
  const selectedShiftsByRow = groupBy(selectedShifts, (shift: Shift) => shift.rowKeyReference(view))
  const selectedRestDaysByRow = groupBy(selectedRestDays, (restDay: RestDay) =>
    String(restDay.employeeId)
  )

  for (const key in daysByRow) {
    const days = daysGrid[key] || []
    const rowShifts = selectedShiftsByRow[key]
    const rowRestDays = selectedRestDaysByRow[key]
    const daysFromRowShifts = getDaysFromEmployeeShifts(rowShifts, view)
    const daysFromRowRestDays = getDaysFromEmployeeRestDays(rowRestDays, view)
    const selectedDaysWithoutShifts = difference(days, daysFromRowShifts)
    const selectedDaysWithoutRestDays = difference(selectedDaysWithoutShifts, daysFromRowRestDays)

    if (selectedDaysWithoutRestDays.length > 0) {
      cellCount += selectedDaysWithoutRestDays.length
    }
  }

  return cellCount
}

export { countCellsSelected }
